import { useCallback, useEffect } from 'react';

import { SkyscannerRadarSearchV1Search } from '@skyscanner-internal/radar-web-http-client';
import { useDispatch, useSelector } from 'react-redux';

import getFeatureDecision from '../../../../../config';
import { ENABLE_DEAL_HUNTER_LITE } from '../../../../../config/constants';
import {
  setRadarPollAttempted,
  setRadarPollCompleted,
  setRadarResponseReceived,
  setRadarSessionStarted,
} from '../../../actions/deal-hunter-actions';
import { trackInteractionEvent } from '../../../helpers/logging/trackInteractionEvent';
import {
  getDealHunterSessionForDuration,
  getRadarCreateSessionRequest,
  getRadarPollRequest,
} from '../../../selectors/dealHunter';
import { getPreferDirects, getTripDuration } from '../../../selectors/index';

import { MAX_POLL_REQUESTS, POLL_TIMEOUT_MS } from './constants';
import { transformRadarSearchResponse } from './transformRadarSearchResponse';

import type { State } from '../../../selectors/types';

const COMMON_HEADERS = {
  pathPrefix: '/g/radar',
  headers: {
    'Content-Type': 'application/json',
  },
};
type RequestType = 'create' | 'poll';

export const useRadarCreateSession = () => {
  const enableRadarRequest = getFeatureDecision(ENABLE_DEAL_HUNTER_LITE, false);
  const dispatch = useDispatch();
  const createSessionRequest = useSelector(getRadarCreateSessionRequest);
  const tripDuration = useSelector(getTripDuration);
  const preferDirects = useSelector(getPreferDirects);
  const pollRequest = useSelector((state: State) =>
    getRadarPollRequest(state, tripDuration),
  );
  const sessionForDuration = useSelector((state: State) =>
    getDealHunterSessionForDuration(state, tripDuration),
  );

  // Use Redux pollCount for controlling the loop
  const pollCount = sessionForDuration?.pollCount ?? 0;

  const createOrPoll = useCallback(
    async (mode: RequestType): Promise<void> => {
      const request = mode === 'create' ? createSessionRequest : pollRequest;
      if (!request || !tripDuration) return;

      // Track poll attempt at the start (for accurate progress bar)
      if (mode === 'poll') {
        dispatch(setRadarPollAttempted(tripDuration));
      }

      try {
        if (mode === 'create') {
          dispatch(setRadarSessionStarted(tripDuration));
          trackInteractionEvent({
            name: 'Itinerary_Loading_Started',
            strings: {
              tripDuration: tripDuration.toString(),
            },
            bools: {
              preferDirect: preferDirects,
            },
          });
        }

        const response =
          await SkyscannerRadarSearchV1Search.SearchService.Search(
            request,
            COMMON_HEADERS,
          );

        const {
          cheapestDirectItinerary,
          cheapestItinerary,
          pollingSessionComplete,
          pollingSessionToken,
        } = transformRadarSearchResponse(response);

        if (cheapestItinerary) {
          dispatch(
            setRadarResponseReceived(
              tripDuration,
              cheapestItinerary,
              mode === 'create' ? pollingSessionToken : undefined,
              cheapestDirectItinerary,
            ),
          );
        }

        if (pollingSessionComplete) {
          dispatch(setRadarPollCompleted(tripDuration));
        }
      } catch {
        // On error, mark session as complete to prevent infinite retries
        dispatch(setRadarPollCompleted(tripDuration));
      }
    },
    [dispatch, createSessionRequest, pollRequest, tripDuration, preferDirects],
  );

  // Effect to mark session complete when poll budget is exhausted
  useEffect(() => {
    if (
      tripDuration &&
      sessionForDuration?.status === 'incomplete' &&
      pollCount >= MAX_POLL_REQUESTS
    ) {
      dispatch(setRadarPollCompleted(tripDuration));
    }
  }, [dispatch, sessionForDuration, pollCount, tripDuration]);

  useEffect(() => {
    if (!enableRadarRequest || !tripDuration) return undefined;
    if (sessionForDuration?.status === 'complete') return undefined;
    if (pollCount >= MAX_POLL_REQUESTS) return undefined;

    const mode = sessionForDuration ? 'poll' : 'create';
    const timeout = mode === 'create' ? 0 : POLL_TIMEOUT_MS;

    const timeoutId = setTimeout(() => {
      createOrPoll(mode);
    }, timeout);

    return () => clearTimeout(timeoutId);
  }, [
    createOrPoll,
    sessionForDuration,
    tripDuration,
    enableRadarRequest,
    pollCount,
  ]);
};
